import { useState, useCallback, useEffect } from 'react';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import { useFilesStore } from '@/stores/files';
import { useWorkspaceStore } from '@/stores/workspace';
import { measure } from '@/lib/performance';
import { useWorkspaceStore as useTabsStore } from '@/stores/workspace';

import { simpleHash } from '@/lib/utils';
export const useFileOperations = () => {
  const {
    openFilePath,
    editedContent,
    setEditedContent,
  } = useWorkspaceStore(state => ({ openFilePath: state.openFilePath, editedContent: state.editedContent, setEditedContent: state.setEditedContent }));

  const [isSaving, setIsSaving] = useState(false);
  // Do not get openFilePath from here, it's now passed in.
  const { cachedContents, ...fileActions } = useFilesStore();

  const handleSaveFile = useCallback(measure(async () => {
    if (!openFilePath || editedContent[openFilePath] === undefined || isSaving) return;
    
    setIsSaving(true);
    try {
      await http<void>(`/api/files/content`, {
        method: 'POST',
        body: JSON.stringify({
          path: openFilePath,
          content: editedContent[openFilePath],
        }),
      });
      
      const newContent = editedContent[openFilePath];
      const newHash = simpleHash(newContent);
      fileActions.patch({
        cachedContents: {
          ...cachedContents,
          [openFilePath]: {
            ...(cachedContents?.[openFilePath] || {}),
            content: newContent,
            hash: newHash,
            cachedAt: new Date().toISOString(),
          },
        },
      } as any);
      
      setEditedContent((prev: any) => {
        const next = { ...prev };
        delete next[openFilePath];
        return next;
      });
      
      toast.success(`Saved ${openFilePath}`);
    } catch (error: any) {
      toast.error('Failed to save file', { description: error.message });
    } finally {
      setIsSaving(false);
    }
  }, 'handleSaveFile'), [openFilePath, editedContent, isSaving, fileActions, cachedContents, setEditedContent]);

  // Auto-load file on mount if needed
  useEffect(() => {
    if (openFilePath && cachedContents?.[openFilePath]?.content === undefined) {
      fileActions.loadFile(openFilePath);
    }
  }, [openFilePath, cachedContents, fileActions]);

  const content = openFilePath ? (editedContent[openFilePath] ?? cachedContents?.[openFilePath]?.content ?? '') : '';

  const handleContentChange = useCallback((newContent: string | undefined) => {
    if (!openFilePath) return;
    const tabsStore = useTabsStore.getState();
    const activeTab = tabsStore.tabs.find(t => t.id === openFilePath);
    if (activeTab?.isPreview) {
      tabsStore.promoteCurrentPreview();
    }

    const contentStr = newContent || "";
    const originalHash = cachedContents?.[openFilePath]?.hash;
    const currentHash = simpleHash(contentStr);

    if (originalHash !== undefined && currentHash === originalHash) {
      // Content is the same as original, remove from editedContent if it exists
      setEditedContent(prev => {
        if (prev[openFilePath] === undefined) return prev;
        const next = { ...prev };
        delete next[openFilePath];
        return next;
      });
    } else {
      // Content is different, add/update in editedContent
      setEditedContent(prev => ({
        ...prev,
        [openFilePath]: contentStr,
      }));
    }
  }, [openFilePath, cachedContents, setEditedContent]);

  return {
    handleSaveFile,
    isSaving,
    content,
    fileActions,
    cachedContents,
    handleContentChange,
  };
};